import React, { useState } from 'react';
import { X, Check, Loader2, ExternalLink, CheckCircle, Sparkles } from 'lucide-react';
import apiClient from '../../../lib/apiClient';
import TransactionListModal from '../assessment/TransactionListModal';
import {
  CAPABILITY_MATURITY_DESCRIPTIONS,
  CAPABILITY_VENDOR_MAPPINGS,
  MATURITY_LEVELS,
} from '../config/ztaArchitectureData';

// ============================================================================
// CAPABILITY EDIT MODAL
// ============================================================================

const CapabilityModal = ({ capability, data, suggestion, onClose, onSave, saving, departmentName, departmentId }) => {
  // Transaction modal state
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotalSpend, setTransactionsTotalSpend] = useState(0);

  // Fetch transactions for this capability
  const fetchTransactions = async () => {
    if (!departmentId || !capability.id) return { transactions: [], count: 0, totalSpend: 0 };

    setTransactionsLoading(true);
    try {
      const data = await apiClient.get(
        `/api/zero-trust/departments/${departmentId}/capability/${capability.id}/transactions`
      );
      setTransactions(data.transactions || []);
      setTransactionsTotalSpend(data.totalSpend || 0);
      return data;
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return { transactions: [], count: 0, totalSpend: 0 };
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Get maturity descriptions for this capability
  const maturityDescriptions = CAPABILITY_MATURITY_DESCRIPTIONS[capability.id] || {};

  // Get vendor/product mappings for this capability
  const vendorMapping = CAPABILITY_VENDOR_MAPPINGS[capability.id] || { vendors: [], products: {} };

  // Parse initial vendors - handle both array and string formats
  const getInitialSelectedVendors = () => {
    if (data.vendors?.length) {
      // Parse vendors that might include products like "Okta (Okta SSO)"
      return data.vendors.map(v => {
        const match = v.match(/^([^(]+)(?:\s*\(([^)]+)\))?$/);
        if (match) {
          return { vendor: match[1].trim(), product: match[2]?.trim() || null };
        }
        return { vendor: v.trim(), product: null };
      });
    }
    if (suggestion?.topVendor && !data.vendors?.length) {
      return [{ vendor: suggestion.topVendor, product: suggestion.topProduct || null }];
    }
    return [];
  };

  const [selectedVendors, setSelectedVendors] = useState(getInitialSelectedVendors());
  const [currentVendor, setCurrentVendor] = useState('');
  const [currentProduct, setCurrentProduct] = useState('');
  const [maturity, setMaturity] = useState(data.maturity || 'TRAD.');
  const [status, setStatus] = useState(data.status || 'NOT_STARTED');
  const [notes, setNotes] = useState(data.notes || '');

  // Get available products for selected vendor
  const availableProducts = currentVendor ? (vendorMapping.products[currentVendor] || []) : [];

  // Add vendor/product to selection
  const handleAddVendor = (vendor = currentVendor, product = currentProduct) => {
    if (!vendor) return;

    const newEntry = {
      vendor: vendor,
      product: product || null
    };

    // Check if already exists
    const exists = selectedVendors.some(
      v => v.vendor === newEntry.vendor && v.product === newEntry.product
    );

    if (!exists) {
      setSelectedVendors(prev => [...prev, newEntry]);
    }

    setCurrentVendor('');
    setCurrentProduct('');
  };

  // Auto-add when product is selected
  const handleProductSelect = (product) => {
    setCurrentProduct(product);
    if (product && currentVendor) {
      // Auto-add when product is selected
      handleAddVendor(currentVendor, product);
    }
  };

  // Remove vendor from selection
  const handleRemoveVendor = (index) => {
    setSelectedVendors(selectedVendors.filter((_, i) => i !== index));
  };

  // Map short maturity level to full key for descriptions
  const maturityKeyMap = {
    'TRAD.': 'traditional',
    'INITIAL': 'initial',
    'ADVANCED': 'advanced',
    'OPTIMAL': 'optimal'
  };

  const handleSave = () => {
    // Include any pending selection that wasn't added
    let finalVendors = [...selectedVendors];
    if (currentVendor) {
      const pendingEntry = {
        vendor: currentVendor,
        product: currentProduct || null
      };
      const exists = finalVendors.some(
        v => v.vendor === pendingEntry.vendor && v.product === pendingEntry.product
      );
      if (!exists) {
        finalVendors.push(pendingEntry);
      }
    }

    // Format vendors for storage
    const vendorStrings = finalVendors.map(v =>
      v.product ? `${v.vendor} (${v.product})` : v.vendor
    );

    onSave({
      vendors: vendorStrings,
      maturity,
      status,
      notes
    });
  };

  return (
    <div className="zta-modal-backdrop" onClick={onClose}>
      <div className="zta-modal zta-modal--capability" onClick={e => e.stopPropagation()}>
        <div className="zta-modal-header">
          <div>
            <h2 className="zta-modal-title">{capability.name}</h2>
            <p className="zta-modal-subtitle">{capability.pillarName} Pillar{departmentName && ` · ${departmentName}`}</p>
          </div>
          <button className="zta-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="zta-modal-body zta-modal-body--scrollable">
          {/* Capability Description */}
          {maturityDescriptions.description && (
            <div className="zta-capability-description">
              <p>{maturityDescriptions.description}</p>
            </div>
          )}

          {/* Suggestion Banner */}
          {suggestion?.detected?.length > 0 && !data.vendors?.length && (
            <div className="zta-suggestion-banner">
              <div className="zta-suggestion-banner-content">
                <Sparkles size={14} />
                <span>
                  Based on purchase history: <strong>{suggestion.topVendor}</strong>
                  {suggestion.topProduct && <> ({suggestion.topProduct})</>}
                  {' '}- ${Math.round((suggestion.totalSpend || 0)).toLocaleString()} spent
                </span>
              </div>
              {departmentId && (
                <button
                  className="zta-suggestion-view-btn"
                  onClick={() => setShowTransactions(true)}
                  title="View supporting transactions"
                >
                  <ExternalLink size={12} />
                  View Transactions
                </button>
              )}
            </div>
          )}

          {/* Maturity Level Selection */}
          <div className="zta-maturity-section">
            <h3>Select Current Maturity Level</h3>
            <p className="zta-maturity-instruction">Click on a level to see what it means and select your current state</p>

            <div className="zta-maturity-cards">
              {MATURITY_LEVELS.map((level, i) => {
                const key = maturityKeyMap[level];
                const description = maturityDescriptions[key];
                const isSelected = maturity === level;

                return (
                  <button
                    key={level}
                    className={`zta-maturity-card ${isSelected ? 'zta-maturity-card--selected' : ''}`}
                    onClick={() => setMaturity(level)}
                    data-level={i}
                  >
                    <div className="zta-maturity-card-header">
                      <span className="zta-maturity-card-level">{level}</span>
                      {isSelected && <CheckCircle size={16} className="zta-maturity-card-check" />}
                    </div>
                    <p className="zta-maturity-card-desc">
                      {description || `${level} maturity level`}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Vendors / Products Selection */}
          <div className="zta-modal-field">
            <label>Assigned Vendors / Products</label>

            {/* Selected vendors chips */}
            {selectedVendors.length > 0 && (
              <div className="zta-vendor-chips">
                {selectedVendors.map((v, idx) => (
                  <span key={idx} className="zta-vendor-chip">
                    {v.vendor}
                    {v.product && <span className="zta-vendor-chip-product"> ({v.product})</span>}
                    <button
                      type="button"
                      className="zta-vendor-chip-remove"
                      onClick={() => handleRemoveVendor(idx)}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Vendor/Product selection dropdowns */}
            <div className="zta-vendor-selector">
              <select
                value={currentVendor}
                onChange={e => {
                  const value = e.target.value;
                  setCurrentVendor(value);
                  setCurrentProduct(''); // Reset product when vendor changes
                  // If "None" selected, clear all vendors and auto-add
                  if (value === 'None') {
                    setSelectedVendors([{ vendor: 'None', product: null }]);
                    setCurrentVendor('');
                  }
                }}
                className="zta-modal-select"
              >
                <option value="">Select a vendor...</option>
                <option value="None">None (No vendor in place)</option>
                {vendorMapping.vendors.map(vendor => (
                  <option key={vendor} value={vendor}>{vendor}</option>
                ))}
              </select>

              {availableProducts.length > 0 && (
                <select
                  value={currentProduct}
                  onChange={e => handleProductSelect(e.target.value)}
                  className="zta-modal-select"
                >
                  <option value="">Select a product (optional)...</option>
                  {availableProducts.map(product => (
                    <option key={product} value={product}>{product}</option>
                  ))}
                </select>
              )}

              <button
                type="button"
                className="zta-vendor-add-btn"
                onClick={() => handleAddVendor()}
                disabled={!currentVendor}
              >
                Add
              </button>
            </div>

            <span className="zta-modal-hint">Select a vendor, then a product (auto-adds) or click Add for vendor only</span>
          </div>

          {/* Implementation Status */}
          <div className="zta-modal-field">
            <label>Implementation Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="zta-modal-select"
            >
              <option value="NOT_STARTED">Not Started</option>
              <option value="PLANNING">Planning</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DEPLOYED">Deployed</option>
            </select>
          </div>

          {/* Notes */}
          <div className="zta-modal-field">
            <label>Notes / Configuration Details</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Implementation notes, configuration details, blockers..."
              className="zta-modal-textarea"
              rows={3}
            />
          </div>
        </div>

        <div className="zta-modal-footer">
          <button className="zta-modal-btn zta-modal-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="zta-modal-btn zta-modal-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
            Save Changes
          </button>
        </div>

        {/* Transaction List Modal */}
        <TransactionListModal
          isOpen={showTransactions}
          onClose={() => setShowTransactions(false)}
          capabilityCode={capability.id}
          capabilityName={capability.name}
          vendor={suggestion?.topVendor}
          product={suggestion?.topProduct}
          fetchTransactions={fetchTransactions}
        />
      </div>
    </div>
  );
};

export default CapabilityModal;
